/**
 * NotificationPreferencesDialog — edits the caller's notification preferences
 * (LOS GET/PUT /notifications/preferences). Covers the §4 schema the backend
 * accepts: per-type channel matrix (live / email), a severity floor, and
 * quiet hours. Subscriptions + muteDeals are preserved untouched (we don't send
 * those keys, and the backend only $sets provided keys).
 *
 * The INBOX (bell) is never preference-gated — these settings only govern how a
 * notification is PUSHED (live toast / email).
 */
import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import { useSelector } from "react-redux";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { useNotificationsHook } from "../../hooks/useNotificationsHook";

// Notification types surfaced in the matrix, grouped for readability. Keys match
// the backend notification `type` strings (notifications-and-presence.md §2).
const TYPE_GROUPS = [
  {
    label: "Activity",
    types: [
      { key: "comment_added", label: "Comments & @mentions" },
      { key: "condition_comment", label: "Comment on a condition" },
      { key: "exception_comment", label: "Comment on an exception" },
      { key: "task_comment", label: "Comment on a task" },
      { key: "loan_changed", label: "Loan field updates" },
      { key: "stage_changed", label: "Loan stage changes" },
      { key: "assigned_to_loan", label: "Assigned to a loan" },
      { key: "role_assigned", label: "Assigned a role on a loan" },
    ],
  },
  {
    label: "Underwriting",
    types: [
      { key: "condition_added", label: "Condition added" },
      { key: "condition_updated", label: "Condition updated" },
      { key: "condition_cleared", label: "Condition cleared" },
      { key: "exception_requested", label: "Exception requested" },
      { key: "exception_updated", label: "Exception updated" },
      { key: "exception_assigned", label: "Exception assigned to you" },
      { key: "exception_decided", label: "Exception decided" },
      { key: "document_uploaded", label: "Document uploaded" },
      {
        key: "document_processor_reviewed",
        label: "Document processor reviewed",
      },
      { key: "document_accepted", label: "Document accepted" },
      { key: "document_rejected", label: "Document rejected" },
      { key: "document_status_changed", label: "Document status changed" },
    ],
  },
  {
    label: "Capital markets & tasks",
    types: [
      { key: "lock_requested", label: "Rate lock requested" },
      { key: "lock_confirmed", label: "Rate lock confirmed" },
      { key: "task_updated", label: "Task updated" },
      { key: "task_completed", label: "Task completed" },
      { key: "tasklist_assigned", label: "Task list assigned" },
      { key: "ticket_created", label: "Support ticket created" },
      { key: "ticket_assigned", label: "Support ticket assigned" },
    ],
  },
  {
    // Completion pings from the on-demand AI review buttons (see
    // run-agent-reviews.md). The agents run async server-side and notify when
    // they finish, so these are the delivery controls for those pings.
    label: "AI reviews",
    types: [
      { key: "loan_opening_review_complete", label: "Loan Opening review complete" },
      { key: "post_uw_qc_review_complete", label: "Post-UW QC review complete" },
      {
        key: "unexecuted_package_index_complete",
        label: "Unexecuted package indexed",
      },
      { key: "hard_exception_check_complete", label: "Hard exception check complete" },
      { key: "suggested_conditions_complete", label: "Suggested conditions ready" },
      { key: "field_diff_review_complete", label: "Field difference review complete" },
      // Acquisitions-pipeline agents.
      {
        key: "closed_package_review_complete",
        label: "Closed package review complete",
      },
      {
        key: "acq_preclose_review_complete",
        label: "Pre-close acquisition review complete",
      },
      { key: "collateral_search_complete", label: "Collateral search complete" },
      {
        key: "acquisition_review_complete",
        label: "Acquisition review (full) complete",
      },
      // Both pipelines — also fires on its own when a loan enters a closing
      // stage (closing-readiness.md §6).
      {
        key: "closing_readiness_review_complete",
        label: "Closing readiness review complete",
      },
    ],
  },
];

const GLOBAL_DEFAULT = { live: true, email: false };
const CHANNELS = ["live", "email"];
const CHANNEL_LABEL = { live: "Live", email: "Email" };

const SEVERITY_LABEL = {
  info: "Info — everything",
  low: "Low and above",
  med: "Medium and above",
  high: "High only",
};

const ChannelHeaderRow = () => (
  <Flx ac sx={{ px: 1, py: 0.5 }}>
    <Box sx={{ flex: 1 }} />
    {CHANNELS.map((c) => (
      <Box key={c} sx={{ width: 54, textAlign: "center" }}>
        <Txt sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary" }}>
          {CHANNEL_LABEL[c]}
        </Txt>
      </Box>
    ))}
  </Flx>
);

const NotificationPreferencesDialog = ({ open, onClose }) => {
  const { loadPreferences, savePreferences } = useNotificationsHook();
  const { prefsMeta } = useSelector((s) => s.notifications);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Local editable form.
  const [channels, setChannels] = useState({});
  const [severityFloor, setSeverityFloor] = useState("info");
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quiet, setQuiet] = useState({
    tz: "America/New_York",
    start: "20:00",
    end: "07:00",
  });

  const severities = prefsMeta?.severities?.length
    ? prefsMeta.severities
    : ["info", "low", "med", "high"];
  const defaultTypeChannels = prefsMeta?.defaultTypeChannels || {};

  // Load fresh prefs every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    loadPreferences()
      .then((data) => {
        if (!active) return;
        const p = data?.preferences || {};
        setChannels({ ...(p.channels || {}) });
        setSeverityFloor(p.severityFloor || "info");
        if (p.quietHours) {
          setQuietEnabled(true);
          setQuiet({
            tz: p.quietHours.tz || "America/New_York",
            start: p.quietHours.start || "20:00",
            end: p.quietHours.end || "07:00",
          });
        } else {
          setQuietEnabled(false);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, loadPreferences]);

  // Effective channel config for a type = explicit override ?? backend default
  // ?? global default.
  const effective = (typeKey) =>
    channels[typeKey] || defaultTypeChannels[typeKey] || GLOBAL_DEFAULT;

  const toggle = (typeKey, channel) => {
    const cur = effective(typeKey);
    setChannels((prev) => ({
      ...prev,
      [typeKey]: { ...GLOBAL_DEFAULT, ...cur, [channel]: !cur[channel] },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await savePreferences({
        channels,
        severityFloor,
        quietHours: quietEnabled
          ? { tz: quiet.tz, start: quiet.start, end: quiet.end }
          : null,
      });
      onClose?.();
    } catch {
      // savePreferences surfaces its own error toast via dsHttp; keep dialog open.
    } finally {
      setSaving(false);
    }
  };

  const body = useMemo(
    () => (
      <>
        <Txt sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
          Choose how you're notified per event type. Everything always lands in
          your bell; these toggles only control live and email pushes.
        </Txt>

        {TYPE_GROUPS.map((group) => (
          <Box key={group.label} sx={{ mb: 3, mt: 1.5 }}>
            <Flx fw jb ac sx={{ mb: 0 }}>
              <Txt
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "#64748b",
                  mb: 0.25,
                }}
              >
                {group.label}
              </Txt>
              <ChannelHeaderRow />
            </Flx>
            {group.types.map((t) => {
              const cfg = effective(t.key);
              return (
                <Flx
                  key={t.key}
                  ac
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    "&:hover": { background: "#f8fafc" },
                  }}
                >
                  <Txt sx={{ flex: 1, fontSize: 12.5, color: "#0f172a" }}>
                    {t.label}
                  </Txt>
                  {CHANNELS.map((c) => (
                    <Box key={c} sx={{ width: 54, textAlign: "center" }}>
                      <Checkbox
                        size="small"
                        checked={Boolean(cfg[c])}
                        onChange={() => toggle(t.key, c)}
                        sx={{ p: 0.5 }}
                      />
                    </Box>
                  ))}
                </Flx>
              );
            })}
          </Box>
        ))}

        <Divider sx={{ my: 1.5 }} />

        {/* Severity floor */}
        <Flx ac jb sx={{ mb: 1.5 }}>
          <Box>
            <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a" }}>
              Minimum severity
            </Txt>
            <Txt sx={{ fontSize: 11, color: "text.secondary" }}>
              Ignore notifications below this level.
            </Txt>
          </Box>
          <TextField
            select
            size="small"
            value={severityFloor}
            onChange={(e) => setSeverityFloor(e.target.value)}
            sx={{ width: 190 }}
          >
            {severities.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>
                {SEVERITY_LABEL[s] || s}
              </MenuItem>
            ))}
          </TextField>
        </Flx>

        {/* Quiet hours */}
        <Flx ac jb>
          <Box>
            <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a" }}>
              Quiet hours
            </Txt>
            <Txt sx={{ fontSize: 11, color: "text.secondary" }}>
              Suppress email in this window (bell still fills).
            </Txt>
          </Box>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={quietEnabled}
                onChange={(e) => setQuietEnabled(e.target.checked)}
              />
            }
            label=""
            sx={{ mr: 0 }}
          />
        </Flx>
        {quietEnabled && (
          <Flx g={1} sx={{ mt: 1 }}>
            <TextField
              label="From"
              type="time"
              size="small"
              value={quiet.start}
              onChange={(e) =>
                setQuiet((q) => ({ ...q, start: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              sx={{ width: 120 }}
            />
            <TextField
              label="To"
              type="time"
              size="small"
              value={quiet.end}
              onChange={(e) => setQuiet((q) => ({ ...q, end: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 120 }}
            />
            <TextField
              label="Time zone"
              size="small"
              value={quiet.tz}
              onChange={(e) => setQuiet((q) => ({ ...q, tz: e.target.value }))}
              sx={{ flex: 1 }}
            />
          </Flx>
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      channels,
      severityFloor,
      quietEnabled,
      quiet,
      severities,
      defaultTypeChannels,
    ],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a", py: 1.25 }}
      >
        Notification settings
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Flx center sx={{ py: 6 }}>
            <CircularProgress size={24} />
          </Flx>
        ) : (
          body
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="text" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationPreferencesDialog;
